<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '700px' }"
  (onHide)="onHide()"
>
  <ng-template pTemplate="header">
    <div class="flex items-center gap-3">
      <i class="pi pi-dollar text-primary-500 text-2xl"></i>
      <h2 class="text-xl font-bold text-gray-900 dark:text-white">
        {{ 'reservations.payments.modalTitle' | translate }}
      </h2>
    </div>
  </ng-template>

  <!-- Resumen de Deuda -->
  <div class="mb-6">
    <p-card>
      <div class="grid grid-cols-3 gap-4 text-center">
        <div>
          <div class="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {{ 'reservations.payments.summary.total' | translate }}
          </div>
          <div class="text-2xl font-bold text-gray-900 dark:text-white">
            {{ totalAmount | currency: 'USD' : 'symbol' : '1.2-2' }}
          </div>
        </div>
        <div>
          <div class="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {{ 'reservations.payments.summary.paid' | translate }}
          </div>
          <div class="text-2xl font-bold text-green-600">
            {{ paidAmount | currency: 'USD' : 'symbol' : '1.2-2' }}
          </div>
        </div>
        <div>
          <div class="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {{ 'reservations.payments.summary.remaining' | translate }}
          </div>
          <div class="text-2xl font-bold text-orange-600">
            {{ remainingBalance | currency: 'USD' : 'symbol' : '1.2-2' }}
          </div>
        </div>
      </div>

      <!-- Saldo después del pago -->
      @if (paymentForm.get('amount')?.value > 0) {
        <div class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div class="flex justify-between items-center">
            <span class="text-sm font-medium text-blue-900 dark:text-blue-100">
              {{ 'reservations.payments.summary.balanceAfterPayment' | translate }}
            </span>
            <span
              class="text-lg font-bold"
              [class.text-green-600]="balanceAfterPayment <= 0"
              [class.text-orange-600]="balanceAfterPayment > 0"
            >
              {{ balanceAfterPayment | currency: 'USD' : 'symbol' : '1.2-2' }}
            </span>
          </div>
          @if (balanceAfterPayment <= 0) {
            <div class="mt-2 text-sm text-green-700 dark:text-green-400">
              {{ 'reservations.payments.summary.completesReservation' | translate }}
            </div>
          }
        </div>
      }
    </p-card>
  </div>

  <!-- Historial de Pagos -->
  @if (paymentHistory.length > 0) {
    <div class="mb-6">
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        {{ 'reservations.payments.history.title' | translate: { count: paymentHistory.length } }}
      </h3>
      <div
        class="space-y-2 max-h-64 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        @for (payment of paymentHistory; track payment) {
          <div
            class="flex justify-between items-start p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 gap-3"
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-sm font-medium text-gray-900 dark:text-white">
                  ${{ payment.amount }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400">({{ payment.type }})</span>
              </div>
              <div class="text-xs text-gray-500">
                {{ payment.created_at | date: 'dd/MM/yyyy HH:mm' }}
              </div>
            </div>
            <div class="flex-shrink-0" style="min-width: 160px">
              <p-select
                [options]="paymentStatuses"
                [(ngModel)]="payment.status"
                (onChange)="onPaymentStatusChange(payment)"
                optionValue="value"
                appendTo="body"
                styleClass="w-full text-sm"
                [panelStyle]="{ 'max-height': '300px' }"
                [placeholder]="'reservations.payments.form.statusPlaceholder' | translate"
              >
                <ng-template let-option pTemplate="item">
                  {{ option.labelKey | translate }}
                </ng-template>
                <ng-template let-option pTemplate="selectedItem">
                  {{ option.labelKey | translate }}
                </ng-template>
              </p-select>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Formulario de Pago -->
  <form [formGroup]="paymentForm" (ngSubmit)="onSubmit()" class="space-y-4">
    <!-- Tipo de Pago -->
    <div class="flex flex-col gap-2">
      <label for="type" class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ 'reservations.payments.form.type' | translate }} *
      </label>
      <p-select
        inputId="type"
        [options]="paymentTypes"
        formControlName="type"
        [placeholder]="'reservations.payments.form.selectType' | translate"
        styleClass="w-full"
      >
        <ng-template let-option pTemplate="item">
          {{ option.labelKey | translate }}
        </ng-template>
        <ng-template let-option pTemplate="selectedItem">
          {{ option.labelKey | translate }}
        </ng-template>
      </p-select>
    </div>

    <!-- Método de Pago y Estado -->
    <div class="grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-2">
        <label for="method" class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ 'reservations.payments.form.method' | translate }} *
        </label>
        <p-select
          inputId="method"
          [options]="paymentMethods"
          formControlName="method"
          [placeholder]="'reservations.payments.form.selectMethod' | translate"
          styleClass="w-full"
        >
          <ng-template let-option pTemplate="item">
            {{ option.labelKey | translate }}
          </ng-template>
          <ng-template let-option pTemplate="selectedItem">
            {{ option.labelKey | translate }}
          </ng-template>
        </p-select>
      </div>

      <div class="flex flex-col gap-2">
        <label for="status" class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ 'reservations.payments.form.status' | translate }} *
        </label>
        <p-select
          inputId="status"
          [options]="paymentStatuses"
          formControlName="status"
          [placeholder]="'reservations.payments.form.selectStatus' | translate"
          styleClass="w-full"
        >
          <ng-template let-option pTemplate="item">
            {{ option.labelKey | translate }}
          </ng-template>
          <ng-template let-option pTemplate="selectedItem">
            {{ option.labelKey | translate }}
          </ng-template>
        </p-select>
      </div>
    </div>

    <!-- Monto y Moneda -->
    <div class="grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-2">
        <label for="amount" class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ 'reservations.payments.form.amount' | translate }} *
        </label>
        <p-inputNumber
          inputId="amount"
          formControlName="amount"
          mode="currency"
          [currency]="paymentForm.get('currency')?.value || 'MXN'"
          locale="es-MX"
          [minFractionDigits]="2"
          [min]="0"
          [max]="remainingBalance"
          styleClass="w-full"
        />
        @if (paymentForm.get('amount')?.invalid && paymentForm.get('amount')?.touched) {
          <small class="text-red-500">
            {{ 'reservations.payments.form.invalidAmount' | translate }}
          </small>
        }
      </div>

      <div class="flex flex-col gap-2">
        <label for="currency" class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ 'reservations.payments.form.currency' | translate }} *
        </label>
        <p-select
          inputId="currency"
          [options]="currencies"
          formControlName="currency"
          [placeholder]="'reservations.payments.form.selectCurrency' | translate"
          styleClass="w-full"
        >
          <ng-template let-option pTemplate="item">
            {{ option.label }}
          </ng-template>
          <ng-template let-option pTemplate="selectedItem">
            {{ option.label }}
          </ng-template>
        </p-select>
      </div>
    </div>

    <!-- Tipo de Cambio y Monto MXN (si es USD) -->
    @if (paymentForm.get('currency')?.value === 'USD') {
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label for="fx_rate" class="text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ 'reservations.payments.form.fxRate' | translate }}
          </label>
          <p-inputNumber
            inputId="fx_rate"
            formControlName="fx_rate"
            [minFractionDigits]="2"
            [maxFractionDigits]="2"
            [min]="0"
            styleClass="w-full"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label for="amount_mxn" class="text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ 'reservations.payments.form.amountMxn' | translate }}
          </label>
          <p-inputNumber
            inputId="amount_mxn"
            formControlName="amount_mxn"
            mode="currency"
            currency="MXN"
            locale="es-MX"
            [minFractionDigits]="2"
            [disabled]="true"
            styleClass="w-full"
          />
        </div>
      </div>
    }

    <!-- Referencia Externa (Opcional) -->
    <div class="flex flex-col gap-2">
      <label for="external_ref" class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ 'reservations.payments.form.externalRef' | translate }}
      </label>
      <input
        id="external_ref"
        type="text"
        pInputText
        formControlName="external_ref"
        [placeholder]="'reservations.payments.form.externalRefPlaceholder' | translate"
        class="w-full"
      />
    </div>

    <!-- Botones -->
    <div class="flex justify-end gap-3 pt-4">
      <p-button
        type="button"
        (onClick)="onCancel()"
        [label]="'common.cancel' | translate"
        severity="secondary"
        [outlined]="true"
      />
      <p-button
        type="submit"
        [disabled]="paymentForm.invalid"
        icon="pi pi-check"
        [label]="'reservations.payments.modalTitle' | translate"
      />
    </div>
  </form>
</p-dialog>
